import { Star } from '@phosphor-icons/react';
import React, { useState, useEffect } from 'react';

export const MEAT_TYPE_IMAGES = {
  'Boeuf': 'https://images.unsplash.com/photo-1733700469173-15d46efc2c09?w=120&h=120&fit=crop&crop=center',
  'Agneau': 'https://images.pexels.com/photos/31732111/pexels-photo-31732111.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Poulet': 'https://images.unsplash.com/photo-1772957041453-e0eb938535a8?w=120&h=120&fit=crop&crop=center',
  'Porc': 'https://images.pexels.com/photos/7333264/pexels-photo-7333264.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Charcuterie': 'https://images.unsplash.com/photo-1614473491229-17bf47b9e487?w=120&h=120&fit=crop&crop=center',
  'Braseros': 'https://images.pexels.com/photos/36318468/pexels-photo-36318468.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Canard': 'https://images.pexels.com/photos/24038062/pexels-photo-24038062.jpeg?auto=compress&w=120&h=120&fit=crop',
  'Kamados': 'https://images.unsplash.com/photo-1770318724110-c9d426f2125e?w=120&h=120&fit=crop&crop=center',
  'Accessoires': 'https://images.unsplash.com/photo-1560614382-33bd4daa1b9e?w=120&h=120&fit=crop&crop=center',
};

export const SHOP_VIDEO_ID_DEFAULT = 'dVM75L__88s';

// Category hierarchy matching magere-viandes.fr structure
// "Featured" categories shown as highlight grid at the top
export const FEATURED_CATEGORY_SLUGS = [
  'specialites', 'les-colis', 'nos-viandes-dexception', 'braseros', 'kamados', 'accessoires', 'special-fetes'
];

// Meat/food categories with their subcategories (in correct display order)
export const CATEGORY_HIERARCHY = [
  { slug: 'le-boeuf', children: ['boeuf-griller', 'boeuf-mijoter'] },
  { slug: 'la-volaille', children: ['fermi-re-label-rouge', 'canard', 'la-volaille-ordinaire'] },
  { slug: 'agneau', children: ['agneau-griller', 'agneau-mijoter'] },  // parent may not exist, use slug match
  { slug: 'le-porc', children: [] },
  { slug: 'veau', children: ['veau-griller', 'veau-mijoter'] },
  { slug: 'la-charcuterie', children: ['charcuterie-ib-rique', 'les-terrines', 'charcuterie-enti-re', 'les-saucissons', 'la-charcuterie-tranch-e'] },
  { slug: 'picerie', children: ['bocaux-et-plats-cuisin-s', 'sauces', 'sauces-piquantes', 'sauces-barbecue', 'ap-ritifs', 'assaisonnements', 'vin'] },
];

// Helper: find a category by slug (exact or partial match)
export const findCatBySlug = (cats, slug) => {
  return cats.find(c => c.slug === slug) || cats.find(c => (c.slug || '').includes(slug)) || null;
};

// Build ordered category tree from flat categories list
export const buildCategoryTree = (allCategories, products) => {
  const featured = [];
  const meats = [];
  const used = new Set();

  // 1. Featured categories
  for (const slug of FEATURED_CATEGORY_SLUGS) {
    const cat = findCatBySlug(allCategories, slug);
    if (cat) {
      featured.push(cat);
      used.add(cat.slug);
    }
  }

  // 2. Meat/food hierarchies
  for (const h of CATEGORY_HIERARCHY) {
    const parent = findCatBySlug(allCategories, h.slug);
    if (parent) {
      const children = h.children.map(cs => findCatBySlug(allCategories, cs)).filter(Boolean);
      meats.push({ parent, children });
      used.add(parent.slug);
      children.forEach(c => used.add(c.slug));
    }
  }

  // 3. Remaining categories (not in featured or hierarchy)
  const remaining = allCategories.filter(c => !used.has(c.slug));

  return { featured, meats, remaining };
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const resolveImage = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

export const getCategoryImage = (cat, catProducts) => {
  // 1. Category's own image (image_url from sync, fallback to legacy `image`)
  const ownImage = cat.image_url || cat.image;
  if (ownImage) return resolveImage(ownImage);
  // 2. Curated unsplash fallback by name
  if (MEAT_TYPE_IMAGES[cat.name]) return MEAT_TYPE_IMAGES[cat.name];
  const lowerName = cat.name.toLowerCase();
  for (const [key, url] of Object.entries(MEAT_TYPE_IMAGES)) {
    const k = key.toLowerCase();
    if (lowerName.includes(k) || (lowerName.length >= 4 && k.includes(lowerName))) return url;
  }
  // 3. Borrow from first product image
  if (catProducts?.length) {
    const fi = catProducts[0]?.featured_image || catProducts[0]?.images?.[0];
    return resolveImage(fi);
  }
  return null;
};

// Returns true if product belongs to this category (or any of its descendant categories)
export const productMatchesCategory = (product, cat, allCategories) => {
  if (!product || !cat) return false;
  // Build set of valid ids: the category + all descendants
  const validIds = new Set([cat.id]);
  const queue = [cat.id];
  while (queue.length) {
    const parentId = queue.shift();
    for (const ch of allCategories) {
      if (ch.parent_id === parentId && !validIds.has(ch.id)) {
        validIds.add(ch.id);
        queue.push(ch.id);
      }
    }
  }
  // Match either via category_ids array OR legacy single-string category
  const pCatIds = product.category_ids || [];
  if (pCatIds.some(id => validIds.has(id))) return true;
  if (product.category) {
    // Build name set for fallback string match
    const nameSet = new Set();
    for (const c of allCategories) if (validIds.has(c.id)) nameSet.add(c.name);
    return nameSet.has(product.category);
  }
  return false;
};

export const productsInCategory = (products, cat, allCategories) =>
  (products || []).filter(p => productMatchesCategory(p, cat, allCategories));

export const getEffectivePrice = (p, isPro) => {
  if (isPro && p.price_pro) return parseFloat(p.price_pro);
  if (p.is_promo && p.sale_price) return parseFloat(p.sale_price);
  return parseFloat(p.price);
};

export const hasProPrice = (p, isPro) => isPro && p.price_pro && parseFloat(p.price_pro) < parseFloat(p.price);

export const getPricePerKg = (product) => {
  const name = (product.name || '').toUpperCase();
  const kgMatch = name.match(/(\d+(?:[.,]\d+)?)\s*KG/);
  if (!kgMatch) return null;
  const kgVal = parseFloat(kgMatch[1].replace(',', '.'));
  if (kgVal <= 0) return null;
  const price = parseFloat(product.is_promo && product.sale_price ? product.sale_price : product.price);
  return (price / kgVal).toFixed(2);
};

export const useCountdown = (endDate) => {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    if (!endDate) return;
    const tick = () => {
      const diff = new Date(endDate) - new Date();
      if (diff <= 0) { setTimeLeft({ d: 0, h: 0, m: 0, s: 0 }); return; }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);
  return timeLeft;
};

export const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={16} weight="fill" className={i <= rating ? 'text-amber-400' : 'text-slate-200'} />
    ))}
  </div>
);
